import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import {
  REVEAL_MARGIN,
  clipRevealVariants,
  lineContainerVariants,
  lineRevealVariants,
} from './variants'
import { useCompactViewport } from './useMediaQuery'

type HeadingTag = 'h1' | 'h2' | 'h3' | 'p' | 'div'

export interface TextRevealProps {
  /** Explicit lines: each rises out of its own line box with a stagger. */
  lines?: React.ReactNode[]
  /** Fallback content for the block (clip-path) reveal when line breaks aren't controlled. */
  children?: React.ReactNode
  as?: HeadingTag
  className?: string
}

/**
 * Scroll-triggered display-heading reveal (marketing site only).
 * With `lines`, each line rises from an overflow-hidden box, staggered;
 * otherwise the whole block wipes up via clip-path. Fires once.
 * Phone-sized viewports render statically (iOS Safari first-frame flicker),
 * and <MotionConfig reducedMotion="user"> removes the motion globally.
 */
export function TextReveal({ lines, children, as = 'h2', className }: TextRevealProps) {
  const compact = useCompactViewport()
  const Tag = as

  if (compact) {
    return (
      <Tag className={className}>
        {lines ? lines.map((line, i) => <span key={i} className="block">{line}</span>) : children}
      </Tag>
    )
  }

  if (lines) {
    return (
      <motion.div
        variants={lineContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: REVEAL_MARGIN }}
      >
        <Tag className={className}>
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span className="block will-change-transform" variants={lineRevealVariants}>
                {line}
              </motion.span>
            </span>
          ))}
        </Tag>
      </motion.div>
    )
  }

  return (
    <motion.div
      variants={clipRevealVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: REVEAL_MARGIN }}
      className={cn('will-change-[clip-path]')}
    >
      <Tag className={className}>{children}</Tag>
    </motion.div>
  )
}

export default TextReveal
